import { BehaviorSubject } from 'rxjs'
import { Configuration, Person, PersonApi } from '../api'
import { appState } from './App.state'
import { getAppConfig } from '../config/AppConfig'

const plainCfg = () => new Configuration({ basePath: getAppConfig().basePath })

interface AuthState {
    user$: BehaviorSubject<Person | null>
    accessToken$: BehaviorSubject<string | undefined>
    igvNumber$: BehaviorSubject<number | null>
    guestMode$: BehaviorSubject<boolean>
    apicfg: () => Configuration
    login: (igvKennung: number, password: string) => Promise<boolean>
    loginAsGuest: () => void
    logout: () => void
    tryAutoLogin: () => void
    refreshAccessToken: () => Promise<boolean>
    isTokenExpiringSoon: () => boolean
    reloadUser: (overrides?: Partial<Person>) => Promise<void>
}

export const handleUnauthorized = (status: number) => {
    if (status === 401 || status === 403) {
        authState.logout()
        window.location.hash = '#/home'
    }
}

export const authState: AuthState = {
    user$: new BehaviorSubject<Person | null>(null),
    accessToken$: new BehaviorSubject<string | undefined>(undefined),
    guestMode$: new BehaviorSubject<boolean>(false),
    igvNumber$: new BehaviorSubject<number | null>(
        localStorage.getItem('igvNumber') ? parseInt(localStorage.getItem('igvNumber')!, 10) : null
    ),

    isTokenExpiringSoon: () => {
        const token = authState.accessToken$.value
        if (!token) return true
        try {
            const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
            const payload = JSON.parse(atob(base64))
            return (payload.exp * 1000) - Date.now() < 5 * 60_000
        } catch {
            return false
        }
    },

    apicfg: () => {
        if (authState.guestMode$.value) {
            return new Configuration({
                basePath: getAppConfig().basePath,
                apiKey: localStorage.getItem('guestApiKey') ?? '',
            })
        }
        return new Configuration({
            basePath: getAppConfig().basePath,
            middleware: [{
                pre: async (context) => {
                    const isAuthEndpoint = context.url.includes('/api/Person/login')
                        || context.url.includes('/api/Person/refresh')
                        || context.url.includes('/api/Person/anonymous')
                    if (!isAuthEndpoint && authState.isTokenExpiringSoon()) {
                        await authState.refreshAccessToken()
                    }
                    const token = authState.accessToken$.value
                    if (token) {
                        return {
                            url: context.url,
                            init: {
                                ...context.init,
                                headers: {
                                    ...(context.init.headers as Record<string, string>),
                                    Authorization: `Bearer ${token}`,
                                },
                            },
                        }
                    }
                },
                post: async (context) => {
                    if (context.response.status === 401 || context.response.status === 403) {
                        const refreshed = await authState.refreshAccessToken()
                        if (!refreshed) {
                            handleUnauthorized(context.response.status)
                        }
                    }
                    return context.response
                },
            }],
        })
    },

    login: async (igvKennung: number, password: string) => {
        try {
            const raw = await new PersonApi(plainCfg()).apiPersonLoginPostRaw({ loginRequestDto: { igvKennung, password } })
            const data = await raw.raw.json()
            // merge raw response fields not in swagger schema
            const user = { ...data, showId: data.showId ?? data.actualShow ?? null, database: data.database ?? null }
            authState.accessToken$.next(data.apiKey ?? undefined)
            authState.igvNumber$.next(igvKennung)
            localStorage.setItem('refreshToken', data.refreshToken ?? '')
            localStorage.setItem('igvNumber', String(igvKennung))
            localStorage.setItem('guestApiKey', data.guestApiKey ?? '')
            localStorage.setItem('user', JSON.stringify(user))
            authState.user$.next(user)
            window.location.hash = '#/home'
            return true
        } catch (e) {
            appState.addToast('Login fehlgeschlagen!', 'error')
            return false
        }
    },

    loginAsGuest: async () => {
        authState.guestMode$.next(true)
        window.location.hash = '#/history'
    },

    refreshAccessToken: async () => {
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken) return false
        try {
            const raw = await new PersonApi(plainCfg()).apiPersonRefreshPostRaw({ refreshRequestDto: { refreshToken } })
            const data = await raw.raw.json().catch(() => ({}))
            if (data?.accessToken) authState.accessToken$.next(data.accessToken)
            if (data?.refreshToken) localStorage.setItem('refreshToken', data.refreshToken)
            return true
        } catch {
            // nur ausloggen wenn auch das aktuelle Token bereits abgelaufen ist
            const token = authState.accessToken$.value
            if (!token) { authState.logout(); return false }
            try {
                const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
                const payload = JSON.parse(atob(base64))
                if ((payload.exp * 1000) < Date.now()) { authState.logout() }
            } catch { authState.logout() }
            return false
        }
    },

    logout: () => {
        authState.user$.next(null)
        authState.igvNumber$.next(null)
        authState.accessToken$.next(undefined)
        authState.guestMode$.next(false)
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('igvNumber')
        localStorage.removeItem('user')
    },

    reloadUser: async (overrides?: Partial<Person>): Promise<void> => {
        const current = authState.user$.value
        if (!current) return
        const patched: Person = { ...current, ...overrides }
        localStorage.setItem('user', JSON.stringify(patched))
        authState.user$.next(patched)
    },

    tryAutoLogin: () => {
        const refreshToken = localStorage.getItem('refreshToken')
        const userJson = localStorage.getItem('user')
        const igvNumber = localStorage.getItem('igvNumber')
        if (refreshToken) {
            authState.refreshAccessToken().then(success => {
                if (success && userJson) {
                    const parsedUser = JSON.parse(userJson)
                    if (!parsedUser.showId && parsedUser.actualShow) parsedUser.showId = parsedUser.actualShow
                    authState.user$.next(parsedUser)
                    authState.igvNumber$.next(igvNumber ? parseInt(igvNumber, 10) : null)
                }
            })
        }
    },
}

